// Libraries
import { useCallback, useEffect, useState } from 'react';
// Types
import { UseCountdown } from './useCountdown.type';

/**
 * Custom React hook for implementing a countdown timer.
 * @param {number} defaultDuration - The default duration in seconds for the countdown.
 * @returns {UseCountdown} - An object containing the countdown state and control functions.
 */
export const useCountdown = (defaultDuration = 30): UseCountdown => {
  const [timeLeft, setTimeLeft] = useState(0);
  const [isRunning, setIsRunning] = useState(false);

  /**
   * Start the countdown with the specified duration.
   * @param {number} durationInSeconds - The duration in seconds for the countdown.
   */
  const start = useCallback((durationInSeconds: number) => {
    setTimeLeft(durationInSeconds);
    setIsRunning(true);
  }, []);

  /**
   * Reset the countdown to the default duration.
   */
  const reset = useCallback(() => {
    setIsRunning(false);
    setTimeLeft(defaultDuration);
  }, [defaultDuration]);

  useEffect(() => {
    let countdownTimer: NodeJS.Timeout;

    if (isRunning && timeLeft === 0) {
      reset();
    }
    if (isRunning && timeLeft > 0) {
      countdownTimer = setInterval(() => {
        setTimeLeft((prevTime) => prevTime - 1);
      }, 1000);
    }

    return () => {
      clearInterval(countdownTimer);
    };
  }, [isRunning, timeLeft, reset]);

  return { timeLeft, isRunning, start, reset };
};
